import random from "./random.js"
import Queue from "./queue.js"
import errorModifier from "./error_modifier.js"

function addEventCapabilities (object, {parent, nameSpace, onError} = {}) {


    let master = parent || object

    if (!parent) {

        addNameSpacingCapabilities(object)

        object.listenersFor = {}
        object.emitQueue    = []

    }



    // Adds a listener on a certain event
    object.on = function (eventName, callback) {

        if (!master.listenersFor[eventName]) {
            master.listenersFor[eventName] = []
        }

        let trace = new Error(`event : "${eventName}"`)

        let listener = {
            origin: originOfCall(trace), //FIXME remove since it's covered by trace ?
            trace,
            nameSpace,
            callback
        }

        master.listenersFor[eventName].push(listener)
        return listener
    }


    object.when = object.on


    object.once = function (eventName, callback = () => {}) {
        return new Promise(resolve => {
            function internalCallback (...args) {
                object.removeListener(eventName, internalCallback)
                callback(...args)
                resolve(args)
            }
            object.on(eventName, internalCallback)
        })

    }


    object.onceAll = function (events, callback = () => {}) {
        if (Array.isArray(events)) {
            let newEvents = {}
            events.forEach(eventName => {
                newEvents[eventName] = null
            })
            events = newEvents
        }
        let total = Object.keys(events).length
        let triggered = 0
        let allArgs = {}
        return new Promise(resolve => {

            function end () {
                let allArgsList = []
                for (let eventName in events) {
                    allArgsList.push(allArgs[eventName])
                }
                callback(...allArgsList)
                resolve(allArgsList)
            }

            for (let eventName in events) {
                let partialCallback = events[eventName]
                object.once(eventName, (...args) => { //eslint-disable-line no-loop-func
                    if (partialCallback) {
                        partialCallback(...args)
                    }
                    allArgs[eventName] = args
                    triggered += 1
                    if (triggered === total) {
                        end()
                    }
                })
            }
        })

    }


    object.removeListener = function (eventName, callbackOrListener) {

        let listeners = master.listenersFor[eventName]

        if (listeners) {

            let index
            if (typeof callbackOrListener === "function") {
                index = findListenerIndexWithCallback(listeners, callbackOrListener)
            } else {
                index = findListenerIndex(listeners, callbackOrListener)
            }

            if (index >= 0) {
                listeners[index] = null
            }
        }

    }


    object.removeAllListenersFor = function (eventName) {
        if (master.listenersFor[eventName]) {
            master.listenersFor[eventName] = []
        }
    }


    object.removeAllListeners = function () {
        master.listenersFor = {}
    }




    let {emitQueue} = master


    // Triggers all listener on a certain event.
    // Use: emit(eventName, arg1, arg2, arg3, ...)
    object.emit = function (eventName, ...args) {
        let stackTraceRecord = new Error()

        emitQueue.push({
            emitSync,  //used in namespaces
            eventName,
            arguments: args,
            stackTraceRecord,
            stackTraceRecordFromEmitter: object.lastStackTraceRecordFromEmitter
        })

        delete object.lastStackTraceRecordFromEmitter

        if (emitQueue.length === 1) {
            checkQueue()
        }
    }


    function checkQueue () {
        while (emitQueue.length > 0) {
            let firstInQueue = emitQueue[0]
            firstInQueue.emitSync(
                firstInQueue.eventName,
                firstInQueue.arguments,
                firstInQueue.stackTraceRecord,
                firstInQueue.stackTraceRecordFromEmitter
            )
            emitQueue.shift()
        }
    }


    object.enterTestMode = function ({seed, minDelay = 0, maxDelay = 100, keepOrder} = {}) {
        const privateRandom = random.createNew(seed)
        console.log("Event test mode with seed : ", privateRandom.getSeed())

        if (keepOrder) {
            enterOrderedTestMode({privateRandom, minDelay, maxDelay})
        } else {
            let originalEmit = object.emit
            object.emit = function (...args) {
                let delay = privateRandom.intBetween(minDelay, maxDelay)
                setTimeout(() => originalEmit(...args), delay)
            }
        }

    }


    function enterOrderedTestMode ({privateRandom, minDelay, maxDelay}) {
        let originalEmit = object.emit

        let queue = new Queue()

        function handleQueue () {
            queue.handleInSerie(function ({delay, args}, callback) {
                setTimeout(function () {
                    originalEmit(...args)
                    callback()
                }, delay)
            })
        }

        object.emit = function (...args) {
            let delay = privateRandom.intBetween(minDelay, maxDelay)
            let finished = queue.finished()
            queue.add({args, delay})
            if (finished) {
                handleQueue()
            }
        }
    }



    object.emitSync = function (eventName, ...args) {
        console.warn(`Please try to avoid calling emitSync. Here it was called for event '${eventName}'. Note that emitSync won't have the nice callstacks that classic emit has...`)
        emitSync(eventName, args)
    }


    function emitSync (eventName, args, stackTraceRecord, stackTraceRecordFromEmitter) { //eslint-disable-line complexity

        let blockedListenerNameSpaces = master.blockedNameSpaces[nameSpace] || []

        let listeners = master.listenersFor[eventName] || []

        let cleanUp
        for (let i = 0; i < listeners.length; i++) {
            let listener = listeners[i]
            if (listener === null) {
                cleanUp = true
                continue
            }
            if (blockedListenerNameSpaces.indexOf(listener.nameSpace) === -1) {

                errorModifier.executeCallback(() => {
                    listener.callback.stackTraceRecord = stackTraceRecord //FIXME for listener_monitor... violent !
                    listener.callback.stackTraceRecordFromEmitter = stackTraceRecordFromEmitter //FIXME for listener_monitor... violent !
                    listener.callback.apply(master, args)
                }, {
                    trace: listener.trace,
                    handleError: error => {
                        handleError({
                            eventName,
                            error,
                            trace:          listener.trace,
                            listenerOrigin: listener.origin,
                            emitOrigin:     stackTraceRecord
                        })
                    }
                })
            }
        }

        if (cleanUp) {
            for (let i = listeners.length - 1; i >= 0; i--) {
                if (listeners[i] === null) {
                    listeners.splice(i, 1)
                }
            }
        }

    }


    function handleError ({eventName, error, listenerOrigin, emitOrigin}) {
        displayCustomError({
            eventName,
            error,
            listenerOrigin,
            emitOrigin
        })

        if (onError) {
            onError(error)
        } else {
            console.error(error)
        }
    }


    object.emitter = function (eventName, {sync, filter} = {}) {

        let emit
        let stackTraceRecord

        if (sync) {
            emit = object.emitSync
        } else {
            emit = object.emit
            stackTraceRecord = new Error()
        }

        return function emitter (...args) {
            if (filter) {
                for (let i = 0; i < args.length; i++) {
                    args[i] = filter(args[i])
                }
            }
            object.lastStackTraceRecordFromEmitter = stackTraceRecord
            emit.call(object, eventName, ...args)
        }
    }


}





function addNameSpacingCapabilities (object) {

    let nameSpaces = {}
    let blockedNameSpaces = {}

    object.blockedNameSpaces = blockedNameSpaces


    object.block = function ({from, to}) {
        blockedNameSpaces[from] = blockedNameSpaces[from] || []
        blockedNameSpaces[from].push(to)
    }


    object.twoWayBlock = function (nameSpaceA, nameSpaceB) {
        object.block({from: nameSpaceA, to: nameSpaceB})
        object.block({from: nameSpaceB, to: nameSpaceA})
    }


    object.createOrFindEventNameSpace = function (nameSpace) {

        if (!nameSpaces[nameSpace]) {

            nameSpaces[nameSpace] = {}
            addEventCapabilities(nameSpaces[nameSpace], {
                parent: object,
                nameSpace
            })

        }

        return nameSpaces[nameSpace]

    }
}






function findListenerIndex (listeners, listener) {
    for (let i = 0; i < listeners.length; i++) {
        if (listeners[i] === listener) {
            return i
        }
    }
    return -1
}


function findListenerIndexWithCallback (listeners, callback) {
    for (let i = 0; i < listeners.length; i++) {
        if (listeners[i] && listeners[i].callback === callback) {
            return i
        }
    }
    return -1
}


function originOfCall (error) {
    return error.stack ? error.stack.split("\n")[2] : undefined
}


function displayCustomError (data) {
    displayWhereListenerWasAttached(data.eventName, data.listenerOrigin)
    displayWhereEmitWasDone(data.eventName, data.emitOrigin)
}


function displayWhereListenerWasAttached (eventName, origin) {
    let errorMessage = `The error displayed below is on event '${eventName}'.`
    if (origin) {
        errorMessage += `\n  The faulty listener for '${eventName}' was attached : \n${origin}`
    }
    console.warn(errorMessage)
}


function displayWhereEmitWasDone (eventName, origin) {
    if (!origin || !origin.stack) {
        return
    }
    let stack = origin.stack.split("\n").splice(2).join("\n")
    console.warn(`The emit '${eventName}' that triggered the error below was called from : \n${stack}`)
}



export default addEventCapabilities

